import { create } from 'zustand';

import { PublicApiBuild } from '@/models/Build';
import { BuildCacheInfo } from '@/models/BuildCacheInfo';
import BuildStatus from '@/models/BuildStatus';
import GitProvider from '@/models/GitProvider';
import { BuildDetailsPageProps } from '@/pages/BuildDetailsPage/types/BuildDetailsPageProps';

import BuildLogSummary from './types/BuildLogSummary';

export type LogViewerVersion = 'legacy' | 'structured';
export type ExpiringRawLogUrlStatus = 'pending' | 'generating' | 'present' | 'deleted' | 'not_supported';
export type BuildLogSummaryStatus = 'pending' | 'fetching' | 'error' | 'success' | 'deleted';

type BuildSummaryState = {
  logViewVersion: LogViewerVersion;
  logUrlStatus: ExpiringRawLogUrlStatus;
  logSummaryStatus: BuildLogSummaryStatus;
  logSummary?: BuildLogSummary;
};

type Extras = {
  initBuild: PublicApiBuild;
  isViewedAsPublicPage: boolean;
  canViewAddonsAndLoggedIn: boolean;
};

type StoreState = BuildDetailsPageProps & BuildSummaryState & Extras;

const INITIAL_BUILD: PublicApiBuild = {
  abortReason: '',
  branch: '',
  buildNumber: 0,
  creditCost: 0,
  finishedAt: null,
  isOnHold: false,
  isProcessed: false,
  originalBuildParams: {} as Record<string, unknown>,
  pullRequestTargetBranch: '',
  pullRequestViewUrl: '',
  rebuildable: true,
  slug: '',
  startedOnWorkerAt: null,
  status: BuildStatus.Success,
  triggeredWorkflow: '',
  pipelineWorkflowId: '',
  triggeredAt: null,
  triggeredBy: null,
  agentRunSlug: undefined,
  aiRunType: undefined,
};

const useBuildDetailsPageStore = create<StoreState>(() => ({
  // Page props
  app: {
    slug: '',
    title: '',
    type: 'other',
    avatarUrl: '',
    provider: GitProvider.CUSTOM,
    projectType: 'other',
    repositoryUrl: '',
    isPublic: false,
    gitRepoSlug: '',
    gitOwner: '',
    usesRepositoryYml: false,
    privacySetting: 'private',
  },
  abilities: {
    canChangeSettings: false,
    canRebuild: false,
    canViewAddons: false,
    canViewBitriseYml: false,
    canViewBuildArtifacts: false,
    canViewBuildCache: false,
    canViewBuildLogs: false,
  },
  build: INITIAL_BUILD,
  initBuild: INITIAL_BUILD,
  stack: {
    id: '',
    displayName: '',
    deprecatedFrom: null,
  },
  machineType: undefined,
  cacheInfo: undefined,
  remoteAccessData: undefined,
  buildTriggerToken: undefined,
  traceToken: undefined,
  trackingId: '',

  // Calculated fields
  isViewedAsPublicPage: false,
  canViewAddonsAndLoggedIn: false,

  // Build log summary fields
  logViewVersion: 'legacy' as const,
  logUrlStatus: 'pending' as const,
  logSummaryStatus: 'pending' as const,
  logSummary: undefined,

  failedBuildAiState: { disabled: 'by-workspace' },
  aiConfigSignpost: false,
  copyFailureAsPrompt: false,
}));

export function updateBuild(buildUpdates: Partial<PublicApiBuild>) {
  useBuildDetailsPageStore.setState((state) => ({
    ...state,
    build: { ...state.build, ...buildUpdates },
  }));
}

export function updateCacheInfo(cacheInfo: BuildCacheInfo | undefined) {
  useBuildDetailsPageStore.setState({ cacheInfo });
}

export function updateLogSummaryInfo({
  logSummary,
  logSummaryStatus,
}: {
  logSummary?: BuildLogSummary;
  logSummaryStatus: BuildLogSummaryStatus;
}) {
  useBuildDetailsPageStore.setState({ logSummary, logSummaryStatus });
}

export function updateLogViewVersion(logViewVersion: LogViewerVersion) {
  useBuildDetailsPageStore.setState({ logViewVersion });
}

export function updateLogUrlStatus(logUrlStatus: ExpiringRawLogUrlStatus) {
  useBuildDetailsPageStore.setState({ logUrlStatus });
}

export default useBuildDetailsPageStore;
